/**
 * Utility functions for mobile device and app detection
 */

export interface DeviceInfo {
  isMobile: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isDesktop: boolean;
}

/**
 * Detects if the user is on a mobile device
 */
export const detectMobileDevice = (): DeviceInfo => {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  
  // Check for mobile devices
  const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
    userAgent.toLowerCase()
  );
  
  // Check for iOS
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
  
  // Check for Android
  const isAndroid = /android/i.test(userAgent);
  
  // Check for desktop (not mobile)
  const isDesktop = !isMobile && (window.innerWidth >= 768);
  
  return {
    isMobile,
    isIOS,
    isAndroid,
    isDesktop,
  };
};

/**
 * Attempts to detect if the app is installed by trying to open a deep link
 * This is not 100% reliable but works in most cases
 */
export const checkAppInstalled = (deepLinkScheme: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const startTime = Date.now();
    let resolved = false;
    
    // Create a hidden iframe to attempt opening the deep link
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = deepLinkScheme;
    document.body.appendChild(iframe);
    
    // Listen for blur event (app opened) or timeout (app not installed)
    const handleBlur = () => {
      if (!resolved) {
        resolved = true;
        document.body.removeChild(iframe);
        window.removeEventListener('blur', handleBlur);
        resolve(true);
      }
    };
    
    const handleTimeout = () => {
      if (!resolved) {
        resolved = true;
        document.body.removeChild(iframe);
        window.removeEventListener('blur', handleBlur);
        resolve(false);
      }
    };
    
    window.addEventListener('blur', handleBlur);
    
    // If blur doesn't happen within 2 seconds, assume app is not installed
    setTimeout(handleTimeout, 2000);
    
    // Also check if page visibility changes (another indicator app opened)
    const handleVisibilityChange = () => {
      if (document.hidden && !resolved) {
        const elapsed = Date.now() - startTime;
        if (elapsed < 2000) {
          handleBlur();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup visibility listener after timeout
    setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, 2000);
  });
};

/**
 * Opens the app using deep link, or redirects to app store if not installed
 */
export const openAppOrStore = async (
  deepLinkScheme: string,
  iosStoreUrl: string,
  androidStoreUrl: string,
  deviceInfo: DeviceInfo
): Promise<void> => {
  if (deviceInfo.isIOS) {
    // For iOS, try to open the app first
    window.location.href = deepLinkScheme;
    
    // If app doesn't open within 2 seconds, redirect to App Store
    setTimeout(() => {
      window.location.href = iosStoreUrl;
    }, 2000);
  } else if (deviceInfo.isAndroid) {
    // For Android, try to open the app
    window.location.href = deepLinkScheme;
    
    // If app doesn't open within 2 seconds, redirect to Play Store
    setTimeout(() => {
      window.location.href = androidStoreUrl;
    }, 2000);
  } else {
    // Desktop - just open the appropriate store
    if (deviceInfo.isIOS) {
      window.open(iosStoreUrl, '_blank');
    } else {
      window.open(androidStoreUrl, '_blank');
    }
  }
};

/**
 * Opens the app store for download
 */
export const openAppStore = (
  iosStoreUrl: string,
  androidStoreUrl: string,
  deviceInfo: DeviceInfo
): void => {
  if (deviceInfo.isIOS) {
    window.open(iosStoreUrl, '_blank');
  } else if (deviceInfo.isAndroid) {
    window.open(androidStoreUrl, '_blank');
  } else {
    // Desktop - show both options or default to Android
    window.open(androidStoreUrl, '_blank');
  }
};


